import { ChangeEvent, FC, KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@mui/material';
import { useBreakpoints } from 'hooks';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';

import { RenameCardTitleModal } from 'pages/Workspace/components/ProjectCards/CardLowerSection/CardTitleComponent/RenameCardTitleModal';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { ConfirmModal } from 'components/_basic/ConfirmModal/ConfirmModal';
import { TextInput } from 'components/_basic/Form/TextInput';
import ModalContext from 'components/ModalManager/modal-context';

import { getValidatedMetadata } from 'helpers/metadataParser';

import { ICardTitleComponentProps } from './types';

import styles from './CardTitleComponent.module.scss';

const maxLength = 150;

export const CardTitleComponent: FC<ICardTitleComponentProps> = ({
  showInputField,
  title,
  uuid,
  toggleCardTitleField,
  metadata,
  ...restProps
}) => {
  const [inputValue, setInputValue] = useState(title);

  const { t } = useTranslation();

  const { isMobile } = useBreakpoints();

  const { showModal, hideModal } = useContext(ModalContext);

  const cardTitleRef = useRef<HTMLInputElement | null>(null);

  const updateMutation = useUpdateBusinessPlan();

  const { track, isPending } = usePromiseStatus();

  useEffect(() => {
    if (!showInputField) return;
    cardTitleRef.current?.focus();
  }, [showInputField]);

  const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setInputValue(value);
  };

  const handleKeyDown = ({ key }: KeyboardEvent<HTMLInputElement>) => {
    if (key === 'Enter' || key === 'Escape') {
      cardTitleRef.current?.blur();
    }
  };

  const handleSubmit = async (currentValue?: string) => {
    const newTitle = (currentValue ?? inputValue).trim();

    if (!newTitle) {
      setInputValue(title);
      toggleCardTitleField();

      return;
    }

    const params = {
      id: uuid,
      body: {
        title: newTitle,
        metadata: JSON.stringify({ ...getValidatedMetadata(metadata), isFirstTryToSetBusinessToolTitle: false }),
      },
    };

    try {
      await track(updateMutation.mutateAsync(params));
    } catch (error) {
      if (error instanceof Error) {
        setInputValue(title);

        showModal(ConfirmModal, {
          body: t('nameIsAlreadyTaken'),
          onAbort: hideModal,
          onConfirm: hideModal,
          confirmButtonColor: 'primary',
          showCloseButton: false,
          confirmText: t('okay'),
          showAbortButton: false,
          className: styles.notification,
        });
      }
    } finally {
      toggleCardTitleField();
    }
  };

  const handleBlur = async () => {
    await handleSubmit();
  };

  const inputValueBlock = (
    <Typography variant="h6" className={styles.cardTitleStyles}>
      {title || inputValue}
    </Typography>
  );

  if (isMobile) {
    if (showInputField) {
      return (
        <>
          <RenameCardTitleModal
            onChange={setInputValue}
            onSubmit={handleSubmit}
            inputValue={inputValue}
            isPending={isPending}
            maxLength={maxLength}
            onClose={toggleCardTitleField}
          />
          {inputValueBlock}
        </>
      );
    }

    return inputValueBlock;
  }

  if (showInputField) {
    return (
      <TextInput
        className={styles.titleInputField}
        inputRef={cardTitleRef}
        value={inputValue}
        variant="standard"
        maxLength={maxLength}
        inputProps={{
          className: styles.inputFieldProps,
          ...restProps.inputProps,
        }}
        InputProps={{
          ...restProps.InputProps,
        }}
        {...restProps}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return inputValueBlock;
};
